/**
 * Forward Cloudscape theme — maps the stock Cloudscape blue accent onto the Iris action accent.
 *
 * This is the Phase 2 recolour (see docs/design/forward-brand-system.md): instead of only
 * painting the custom header, the brand accent now flows into the STOCK Cloudscape components —
 * links, section headers, primary buttons, focus rings, selected rows, the segmented-control
 * pills — so the whole app reads Iris rather than accidental Cloudscape blue.
 *
 * Every token below is in Cloudscape's themeable set (verified against
 * @cloudscape-design/components/internal/generated/theming for this version). Values use the
 * light-mode Iris ramp; dark-mode split values + applyMode wiring are Phase 3.
 */
import type { Theme } from "@cloudscape-design/components/theming";
import { iris, violet, cornflower } from "@/lib/theme/palette";

const WHITE = "#ffffff";

/** Open Sans (loaded via next/font in layout.tsx as --font-open-sans) with system fallbacks. */
export const FORWARD_FONT_STACK =
  'var(--font-open-sans), "Helvetica Neue", Roboto, Arial, sans-serif';

export const forwardTheme: Theme = {
  tokens: {
    fontFamilyBase: FORWARD_FONT_STACK,

    // Accent + interactive text — section headers, active nav, interactive labels.
    colorTextAccent: iris[600],
    colorTextInteractiveDefault: iris[600],
    colorTextInteractiveHover: iris[700],
    colorTextInteractiveActive: iris[700],

    // Links.
    colorTextLinkDefault: iris[600],
    colorTextLinkHover: iris[700],

    // Status: "Computed" (StatusIndicator info) is the common provenance state on nearly every
    // figure — mute it to a quiet slate so it recedes as metadata and stops competing with the
    // Iris accent. "Reproduced" (success/green) stays meaningful and pops against it.
    colorTextStatusInfo: "#5f6b7a",

    // Link-style buttons (e.g. "View configuration rules") — a separate token family.
    colorTextLinkButtonNormalDefault: iris[600],
    colorTextLinkButtonNormalHover: iris[700],
    colorTextLinkButtonNormalActive: iris[700],

    // Primary buttons — Iris fill, white label (iris[500] clears AA with white text).
    colorBackgroundButtonPrimaryDefault: iris[500],
    colorBackgroundButtonPrimaryHover: iris[600],
    colorBackgroundButtonPrimaryActive: iris[700],
    colorTextButtonPrimaryDefault: WHITE,
    colorTextButtonPrimaryHover: WHITE,
    colorTextButtonPrimaryActive: WHITE,

    // Focus rings.
    colorBorderItemFocused: iris[500],
    colorBorderInputFocused: iris[500],

    // Checkbox / radio / toggle checked.
    colorBackgroundControlChecked: iris[500],

    // Selected rows / nav items — Iris tint + Iris edge.
    colorBackgroundItemSelected: iris[50],
    colorBorderItemSelected: iris[500],

    // SegmentedControl selected segment (the pill toggles).
    colorBackgroundSegmentActive: iris[500],
    colorBorderSegmentActive: iris[500],
    colorTextSegmentActive: WHITE,

    // AppLayout drawer / navigation toggle, selected.
    colorBackgroundLayoutToggleSelectedDefault: iris[500],
    colorBackgroundLayoutToggleSelectedHover: iris[600],
    colorBackgroundLayoutToggleSelectedActive: iris[700],
    colorTextLayoutToggleSelected: WHITE,

    // Surface hierarchy (figure/ground): tone the main content background so white
    // Containers/cards read as cards sitting ON a surface, not white-on-white.
    colorBackgroundLayoutMain: "#f2f2f6",
  },
  contexts: {
    // The TopNavigation renders in the "top-navigation" visual context — brand it Dark
    // Violet with white utilities, so the header is a proper Cloudscape component wearing
    // the Forward brand rather than a bespoke bar. See docs/design/forward-brand-system.md.
    "top-navigation": {
      tokens: {
        colorBackgroundContainerContent: violet[800],
        colorTextAccent: WHITE,
        colorTextInteractiveDefault: WHITE,
        colorTextInteractiveHover: cornflower[400],
        colorTextInteractiveActive: WHITE,
      },
    },
  },
};
